<template>
    <app-dialog
        :open="open"
        :title="$t('Custom timeline entry').toString()"
        size="md"
        :busy="loading.initial || loading.saving"
        @close="$emit('close')"
    >
                    <loading :status="loading.initial || loading.saving"/>
                            <div class="grid grid-cols-6 gap-6">
                                <div class="col-span-6">
                                    <label class="stage-filter-label block mb-2" for="timeline-entry-dialog-title">{{ $t('Title and color') }}</label>
                                    <div class="timeline-entry-title-with-color">
                                        <input
                                            id="timeline-entry-dialog-title"
                                            v-model="entry.title"
                                            class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5 timeline-entry-title-input"
                                            :disabled="!entry.can_edit"
                                            maxlength="255"
                                            type="text"
                                        >
                                        <label class="timeline-entry-color-control" for="timeline-entry-dialog-color">
                                            <span class="timeline-entry-color-frame">
                                                <input
                                                    id="timeline-entry-dialog-color"
                                                    v-model="entry.color"
                                                    class="timeline-entry-color-input"
                                                    :disabled="!entry.can_edit"
                                                    type="color"
                                                >
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <div class="col-span-6">
                                    <label class="stage-filter-label block mb-2" for="timeline-entry-dialog-description">{{ $t('Short description') }}</label>
                                    <textarea
                                        id="timeline-entry-dialog-description"
                                        v-model="entry.short_description"
                                        class="stage-filter-input form-textarea w-full min-h-[120px] text-sm transition duration-150 ease-in-out sm:leading-5"
                                        :disabled="!entry.can_edit"
                                    ></textarea>
                                </div>

                                <div class="col-span-3">
                                    <label class="stage-filter-label block mb-2" for="timeline-entry-dialog-start">{{ $t('Start date') }}</label>
                                    <input
                                        id="timeline-entry-dialog-start"
                                        v-model="entry.starts_at"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        :disabled="!entry.can_edit"
                                        type="date"
                                    >
                                </div>

                                <div class="col-span-3">
                                    <label class="stage-filter-label block mb-2" for="timeline-entry-dialog-end">{{ $t('End date') }}</label>
                                    <input
                                        id="timeline-entry-dialog-end"
                                        v-model="entry.ends_at"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        :disabled="!entry.can_edit"
                                        type="date"
                                    >
                                </div>
                            </div>

        <template #footer>
            <app-dialog-action
                :label="$t('Save').toString()"
                :disabled="!entry.can_edit"
                :loading="loading.saving"
                @click="saveEntry"
            />
        </template>
    </app-dialog>
</template>

<script>
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';
import {UI_COLORS} from '@/utils/ui-colors';

export default {
    name: 'timeline-entry-editor-dialog',
    components: {AppDialog, AppDialogAction},
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        customerView: {
            type: Boolean,
            default: false,
        },
        entryId: {
            type: [Number, String, null],
            default: null,
        },
    },
    emits: ['close', 'saved'],
    data() {
        return {
            loading: {
                initial: false,
                saving: false,
            },
            entry: this.emptyEntry(),
        };
    },
    computed: {
        timelineApiBase() {
            return this.customerView ? 'api/timeline' : 'api/workspace/timeline';
        },
        normalizedEntryId() {
            const value = Number(this.entryId);
            return Number.isInteger(value) && value > 0 ? value : null;
        },
    },
    watch: {
        open: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.loadEntry();
                }
            },
        },
        entryId() {
            if (this.open) {
                this.loadEntry();
            }
        },
    },
    methods: {
        emptyEntry() {
            return {
                id: null,
                title: '',
                short_description: '',
                color: UI_COLORS.text.subtle,
                starts_at: '',
                ends_at: '',
                can_edit: true,
            };
        },
        loadEntry() {
            if (!this.normalizedEntryId) {
                this.entry = this.emptyEntry();
                return;
            }

            this.loading.initial = true;
            axios.get(this.timelineApiBase + '/' + this.normalizedEntryId).then((response) => {
                const payload = response.data || {};
                const entry = payload.entry || {};
                this.entry = {
                    ...this.emptyEntry(),
                    ...entry,
                };
            }).finally(() => {
                this.loading.initial = false;
            });
        },
        saveEntry() {
            if (!this.entry.can_edit) {
                return;
            }

            this.loading.saving = true;
            const payload = {
                title: this.entry.title,
                short_description: this.entry.short_description,
                color: this.entry.color,
                starts_at: this.entry.starts_at,
                ends_at: this.entry.ends_at,
            };

            const request = this.normalizedEntryId
                ? axios.put(this.timelineApiBase + '/' + this.normalizedEntryId, payload)
                : axios.post(this.timelineApiBase, payload);

            request.then((response) => {
                this.$emit('saved', response.data || {});
            }).finally(() => {
                this.loading.saving = false;
            });
        },
    },
};
</script>

<style scoped>
.timeline-entry-title-with-color {
    position: relative;
}

.timeline-entry-title-input {
    padding-right: 44px;
}

.timeline-entry-color-input {
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    border: 0;
    background: transparent;
    appearance: none;
    -webkit-appearance: none;
    cursor: pointer;
}

.timeline-entry-color-frame {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid var(--ui-border-default);
    border-radius: 3px;
    background: #ffffff;
    overflow: hidden;
    flex-shrink: 0;
}

.timeline-entry-color-input::-webkit-color-swatch-wrapper {
    padding: 0;
}

.timeline-entry-color-input::-webkit-color-swatch {
    border: 0;
}

.timeline-entry-color-input::-moz-color-swatch {
    border: 0;
}

.timeline-entry-color-control {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
}
</style>
